// components/LandingReview.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import PoseVisualization from './PoseVisualization.js';
import { useToast } from '../context/ToastContext.js';
import './LandingReview.css';

export default function LandingReview() {
  const toast = useToast();
  const [landings, setLandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [classifyLoading, setClassifyLoading] = useState(false);
  
  // Classification form states
  const [landingQuality, setLandingQuality] = useState('Stick');
  const [entryType, setEntryType] = useState('Direct');
  
  const token = localStorage.getItem('token') || JSON.parse(localStorage.getItem('ams_user') || localStorage.getItem('user') || 'null')?.token;

  const fetchLandings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/athlete-landings?processed=true&labeled=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[LandingReview] API Response:', res.data);
      const data = Array.isArray(res.data) ? res.data : res.data.data || [];
      setLandings(data);
    } catch (err) {
      console.error('Failed to fetch landings:', err);
      setLandings([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLandings();
  }, [fetchLandings]);

  const openReviewForm = (landing) => {
    setReviewingId(landing.id);
    setLandingQuality(landing.landing_type || 'Stick');
    setEntryType('Direct');
  };

  const handleSubmitClassification = async () => {
    if (!reviewingId) return;

    setClassifyLoading(true);
    
    try {
      await axios.post(
        `${API_BASE_URL}/api/label-landing`,
        {
          landing_id: reviewingId,
          landing_type: landingQuality,
          entry: entryType,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Landing classification saved successfully!');
      setReviewingId(null);
      fetchLandings();
    } catch (err) {
      console.error('❌ Classification error:', err);
      toast.error(err.response?.data?.error || 'Failed to save classification.');
    }

    setClassifyLoading(false);
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading landings...</div>;

  // If reviewing a landing
  const reviewingLanding = landings.find(l => l.id === reviewingId);

  if (reviewingLanding) {
    return (
      <div className="classification-modal">
        <div className="classification-header">
          <button 
            onClick={() => setReviewingId(null)}
            className="back-button"
          >
            ← Back to List
          </button>
        </div>

        <div className="classification-content">
          <div className="classification-grid">
            {/* Column 1: 🎥 Original Video */}
            <div className="video-section">
              <h3>🎥 Original Video</h3>
              {reviewingLanding.video_id && (
                <video
                  src={`/videos/${reviewingLanding.video_id}`}
                  crossOrigin="anonymous"
                  controls
                  playsInline
                />
              )}
            </div>

            {/* Column 2: 📊 AI Pose Skeleton (RTMPose) */}
            <div className="pose-section">
              <h3>📊 AI Pose Skeleton</h3>
              {reviewingLanding.pose_json_path && (
                <div style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '8px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                  <PoseVisualization 
                    poseJsonPath={reviewingLanding.pose_json_path}
                    videoUrl={reviewingLanding.video_id ? `/videos/${reviewingLanding.video_id}` : null}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Classification Form */}
          <div className="classification-form">
            <h3>AI Analysis & Manual Review</h3>
            <div className="ai-prediction-box">
              <p><strong>Auto-predicted:</strong> {reviewingLanding.landing_type}</p>
              <p><strong>Entry:</strong> {reviewingLanding.entry}</p>
              
              {/* Quality flags */}
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #bbb' }}>
                <p style={{ marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>Quality Flags:</p>
                {reviewingLanding.over_rotated && (
                  <div style={{ 
                    background: '#fff3cd', 
                    padding: '0.5rem', 
                    borderRadius: '4px', 
                    marginBottom: '0.5rem',
                    borderLeft: '4px solid #ff9800'
                  }}>
                    ⚠️ <strong>Over-rotated detected</strong> - Review rotation carefully
                  </div>
                )}
                {reviewingLanding.short_landing && (
                  <div style={{ 
                    background: '#fff3cd', 
                    padding: '0.5rem', 
                    borderRadius: '4px', 
                    marginBottom: '0.5rem',
                    borderLeft: '4px solid #ff9800'
                  }}>
                    ⚠️ <strong>Short landing detected</strong> - Review torso angle
                  </div>
                )}
                {!reviewingLanding.over_rotated && !reviewingLanding.short_landing && (
                  <div style={{ 
                    background: '#d4edda', 
                    padding: '0.5rem', 
                    borderRadius: '4px', 
                    color: '#155724'
                  }}>
                    ✅ Clean landing detected - No issues flagged
                  </div>
                )}
              </div>
            </div>

            {/* Landing Quality - Large Button Interface */}
            <div className="form-section">
              <div className="form-section-title">
                <span>🎯</span>
                Landing Quality
              </div>
              <div className="button-group">
                <button
                  className={`quick-classify-button ${landingQuality === 'Stick' ? 'selected' : ''}`}
                  onClick={() => setLandingQuality('Stick')}
                >
                  <span className="icon">🎯</span>
                  <span>Stick</span>
                </button>
                <button
                  className={`quick-classify-button ${landingQuality === 'Hop/Step' ? 'selected' : ''}`}
                  onClick={() => setLandingQuality('Hop/Step')}
                >
                  <span className="icon">👟</span>
                  <span>Hop/Step</span>
                </button>
                <button
                  className={`quick-classify-button ${landingQuality === 'Over-rotated' ? 'selected' : ''}`}
                  onClick={() => setLandingQuality('Over-rotated')}
                >
                  <span className="icon">🔄</span>
                  <span>Over-rotated</span>
                </button>
                <button
                  className={`quick-classify-button ${landingQuality === 'Short Landing' ? 'selected' : ''}`}
                  onClick={() => setLandingQuality('Short Landing')}
                >
                  <span className="icon">📉</span>
                  <span>Short Landing</span>
                </button>
              </div>
            </div>

            {/* Entry Type - Large Button Interface */}
            <div className="form-section">
              <div className="form-section-title">
                <span>🤸</span>
                Entry Type
              </div>
              <div className="button-group">
                <button
                  className={`quick-classify-button ${entryType === 'Direct' ? 'selected' : ''}`}
                  onClick={() => setEntryType('Direct')}
                >
                  <span className="icon">➡️</span>
                  <span>Direct</span>
                </button>
                <button
                  className={`quick-classify-button ${entryType === 'Piked' ? 'selected' : ''}`}
                  onClick={() => setEntryType('Piked')}
                >
                  <span className="icon">🔽</span>
                  <span>Piked</span>
                </button>
                <button
                  className={`quick-classify-button ${entryType === 'Layouts' ? 'selected' : ''}`}
                  onClick={() => setEntryType('Layouts')}
                >
                  <span className="icon">🤸</span>
                  <span>Layouts</span>
                </button>
                <button
                  className={`quick-classify-button ${entryType === 'Arabesque' ? 'selected' : ''}`}
                  onClick={() => setEntryType('Arabesque')}
                >
                  <span className="icon">💃</span>
                  <span>Arabesque</span>
                </button>
              </div>
            </div>

            <button
              onClick={handleSubmitClassification}
              disabled={classifyLoading}
              className="save-button"
            >
              {classifyLoading ? 'Saving...' : '✅ Save & Next'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>📊 Review & Correct Landing Classifications</h2>
      {landings.length === 0 ? (
        <p>No unlabeled landings to review.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
          {landings.map(l => (
            <div key={l.id} style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '8px', background: '#fafafa' }}>
              {l.video_id && (
                <video 
                  src={`/videos/${l.video_id}`} 
                  crossOrigin="anonymous"
                  style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '4px', marginBottom: '1rem' }}
                  controls
                />
              )}
              <p><strong>AI Prediction:</strong> {l.landing_type}</p>
              <p><strong>Entry:</strong> {l.entry || 'N/A'}</p>
              <p style={{ fontSize: '0.9rem', color: '#666' }}>
                <strong>Rotation:</strong> {l.over_rotated ? 'Over-rotated' : 'Normal'}
              </p>
              <button
                onClick={() => openReviewForm(l)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginTop: '1rem',
                }}
              >
                Review & Correct
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}